import { useState, useEffect, useCallback } from "react";

interface UseScrollToHighlightOptions {
  /**
   * Scroll behavior when clicking the button
   * @default "smooth"
   */
  behavior?: ScrollBehavior;
  /**
   * Intersection ratio at which the element is considered "in view".
   * 0.5 means at least half the row must be visible.
   * @default 0.5
   */
  threshold?: number;
}

export type ScrollDirection = "up" | "down" | null;

/**
 * Hook to show a "scroll to highlight" button when a highlighted element is
 * scrolled out of view. Uses IntersectionObserver against the scroll container
 * so visibility is read directly from the layout — no manual getBoundingClientRect
 * calls that can race against the initial paint.
 */
export const useScrollToHighlight = (
  highlightedElementRef: React.RefObject<HTMLElement | null>,
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  options: UseScrollToHighlightOptions = {},
) => {
  const { behavior = "smooth", threshold = 0.5 } = options;
  const [showButton, setShowButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);

  useEffect(() => {
    if (!enabled) {
      setShowButton(false);
      setScrollDirection(null);
      return;
    }

    const container = scrollContainerRef.current;
    const element = highlightedElementRef.current;

    if (!container || !element) {
      setShowButton(false);
      setScrollDirection(null);
      return;
    }

    // Short-circuit when the container has no scroll overflow — no point
    // offering a "scroll to" affordance when everything fits on screen.
    if (container.scrollHeight <= container.clientHeight) {
      setShowButton(false);
      setScrollDirection(null);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const isVisible = entry.intersectionRatio >= threshold;
        if (isVisible) {
          setShowButton(false);
          setScrollDirection(null);
          return;
        }
        // Element is off-screen — derive direction from its bounding box
        // relative to the container's.
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        setShowButton(true);
        setScrollDirection(elementRect.top < containerRect.top ? "up" : "down");
      },
      { root: container, threshold: [0, threshold, 1] },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [highlightedElementRef, scrollContainerRef, enabled, threshold]);

  const scrollToHighlight = useCallback(() => {
    const element = highlightedElementRef.current;
    const container = scrollContainerRef.current;

    if (element && container) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const scrollTop = element.offsetTop - container.offsetTop - containerRect.height / 2 + elementRect.height / 2;

      container.scrollTo({
        top: scrollTop,
        behavior,
      });
    }
  }, [highlightedElementRef, scrollContainerRef, behavior]);

  return {
    showButton,
    scrollDirection,
    scrollToHighlight,
  };
};
